<template>
  <component
    :is="componentName"
    :href="activity.url"
    :target="isUrlSameDomain ? '_self' : '_blank'"
    @click="handleNotificationClick"
  >
    <div
      class="flex items-center rounded-md p-2 transition-colors hover:bg-neutral-50"
    >
      <div class="flex items-center">
        <div
          class="bg-primary-500 mr-3 h-2 w-2 rounded-full"
          :class="{ invisible: notification.isRead }"
        />
        <component
          :is="icon.component"
          class="mr-4 h-4 w-4"
          :class="icon.class"
        />
      </div>
      <div
        class="flex grow"
        :class="[
          isInDropdown
            ? 'flex-col'
            : 'flex-col md:flex-row md:items-center md:justify-between md:space-x-8',
        ]"
      >
        <div class="text-sm" v-html="content" />
        <div class="shrink-0 text-xs text-neutral-400">
          {{ formatRelativeDate(activity.time + 'Z') }}
        </div>
      </div>
    </div>
  </component>
</template>

<script setup>
import UserAddIcon from '~icons/lucide/user-plus'
import CheckIcon from '~icons/lucide/check'
import CalendarIcon from '~icons/lucide/calendar-days'
import ChessKingIcon from '~icons/lucide/chess-queen'
import RecommendIcon from '~icons/lucide/message-circle-heart'
import CommentAddIcon from '~icons/lucide/message-square-plus'
import NotAllowedIcon from '~icons/lucide/circle-slash'
import PlusIcon from '~icons/lucide/plus'
import ArchiveIcon from '~icons/lucide/archive'
import { computed, defineOptions } from 'vue'
import { useNotificationsStore } from '@/shared/stores/notifications.js'
import { formatRelativeDate } from '@/shared/utils/dates/date-manager'

defineOptions({
  name: 'BaseNotification',
})

const props = defineProps({
  notification: {
    type: Object,
    required: true,
  },
  isInDropdown: {
    type: Boolean,
    default: false,
  },
})

const activity = computed(() => {
  return props.notification.activities[0]
})
// The row is drawn with v-html, and the data holds text that members and
// staff typed (names, help request titles, the reason of a take-down).
const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const content = computed(() => {
  const {
    userName,
    rank,
    reviewsCount,
    contentType,
    libraryName,
    opportunityName,
    cityName,
    dateLabel,
    rejectionNotes,
    postTitle,
    staffName,
    liveTitle,
    films,
    callTitle,
    holdLabel,
    seatTitle,
    sprintTitle,
  } = Object.fromEntries(
    Object.entries(activity.value.data || {}).map(([key, value]) => [
      key,
      escapeHtml(value),
    ]),
  )
  const disapprovedReason = rejectionNotes
    ? `<span class="mt-1 block text-neutral-500${
        props.isInDropdown ? ' line-clamp-3' : ''
      }">${rejectionNotes}</span>`
    : ''
  const messages = {
    connection_request_received: `<strong>${userName}</strong> just connected with you.`,
    opportunity_accepted:
      'Your help request was <strong>approved</strong> and is now live!',
    opportunity_applied: `<strong>${userName}</strong> applied to your help request.`,
    opportunity_invited: `<strong>${userName}</strong> invited you to apply to <strong>"${opportunityName}"</strong>.`,
    new_rank_achieved: `You've reached <strong>${rank}</strong> rank for achieving ${reviewsCount} reviews. Congratulations!`,
    new_recommendation: `<strong>${userName}</strong> left you a recommendation.`,
    leave_review_reminder: `Leave a review for your call with <strong>${userName}</strong>.`,
    leave_recommendation_reminder: `Leave a recommendation for <strong>${userName}</strong>.`,
    session_rescheduled: `<strong>${userName}</strong> proposed a new time for your session.`,
    session_rescheduled_mentee: `<strong>${userName}</strong> proposed a new time for your session.`,
    session_declined: `Your session with <strong>${userName}</strong> was declined.`,
    session_scheduled: `Your session with <strong>${userName}</strong> was scheduled.`,
    // No longer sent. The text stays for the rows already in members' feeds.
    session_completed: `Your session with <strong>${userName}</strong> was completed.`,
    session_summary_ready: `Your recording and takeaways with <strong>${userName}</strong> are ready.`,
    memory_brief_changed: `Your brief changed after your call with <strong>${userName}</strong>. See the new lines.`,
    session_pending_confirmation: `Did your call with <strong>${userName}</strong> happen?`,
    session_requested: `You received new session request by <strong>${userName}</strong>.`,
    team_member_joined: `<strong>${userName}</strong> joined your team.`,
    content_library_added: `Your ${contentType} was <strong>approved</strong> and added to ${libraryName}.`,
    session_reviewed: `You received a review from <strong>${userName}</strong>.`,
    invite_back_received: `<strong>${userName}</strong> invited you back for another call.`,
    instant_live_turned_off:
      'Someone asked you for a live call while you were away, so we paused <strong>live</strong>.',
    session_declined_mentee: `<strong>${userName}</strong> cancelled the session.`,
    session_cancelled_membership_lapsed: `Your session with <strong>${userName}</strong> was cancelled because their membership ended.`,
    // No longer sent: it was one text for both sides. The text stays for the
    // rows already in members' feeds.
    session_auto_declined: `Your session with <strong>${userName}</strong> was automatically declined.`,
    session_expired_for_mentee: `<strong>${userName}</strong> did not answer in time, so your request expired.`,
    session_expired_for_mentor: `The request from <strong>${userName}</strong> expired with no answer.`,
    opportunity_archived: `The help request <strong>"${opportunityName}"</strong> you applied to is closed.`,
    opportunity_disapproved: `Your help request <strong>"${opportunityName}"</strong> was <strong>disapproved</strong>.${disapprovedReason}`,
    city_speaker_invited: `<strong>${userName}</strong> invited you to speak at an event in ${cityName}.`,
    city_speaker_date_set: `The event in ${cityName} you were invited to speak at has a date: <strong>${dateLabel}</strong>.`,
    city_speaker_accepted: `<strong>${userName}</strong> accepted your invitation to speak.`,
    city_speaker_declined: `<strong>${userName}</strong> declined your invitation to speak.`,
    guest_post_approved: `${staffName} approved your guest post <strong>"${postTitle}"</strong>.`,
    guest_post_changes_asked: `${staffName} asks for one change to <strong>"${postTitle}"</strong>. Read the note.`,
    guest_post_declined: `${staffName} read <strong>"${postTitle}"</strong>. Read the note.`,
    guest_post_live: `Your post <strong>"${postTitle}"</strong> is live on the GrowthMentor blog.`,
    live_starting_soon: `<strong>${userName}</strong> goes live in 15 minutes: <strong>${liveTitle}</strong>.`,
    live_follower_alert: `<strong>${userName}</strong> goes live soon: <strong>${liveTitle}</strong>.`,
    live_approved: `Your live <strong>${liveTitle}</strong> is on the Live page.`,
    live_guest_invited: `<strong>${userName}</strong> invites you on air: <strong>${liveTitle}</strong>.`,
    live_guest_accepted: `<strong>${userName}</strong> accepted. Send <strong>${liveTitle}</strong> to review.`,
    live_guest_declined: `<strong>${userName}</strong> can't make it to <strong>${liveTitle}</strong>. Pick another guest.`,
    live_guest_withdrew: `<strong>${userName}</strong> can't make it, so <strong>${liveTitle}</strong> is cancelled.`,
    live_sent_back: `Staff ask for one change to your live <strong>${liveTitle}</strong>. Read the note.`,
    films_ready: `Your ${films === '1' ? 'film' : `${films} films`} from <strong>${liveTitle}</strong> ${films === '1' ? 'is' : 'are'} ready to share.`,
    group_call_place_open: `A place opened on <strong>${callTitle}</strong>. It is yours until ${holdLabel}.`,
    seat_place_open: `A place opened in <strong>${userName}</strong>'s advisor plan <strong>${seatTitle}</strong>. Ask for it within 48 hours.`,
    sprint_new_order: `<strong>${userName}</strong> bought your sprint <strong>${sprintTitle}</strong>.`,
    sprint_deciding: `The kickoff call of <strong>${sprintTitle}</strong> is over. Go ahead or stop within 24 hours.`,
    sprint_due: `<strong>${sprintTitle}</strong> for <strong>${userName}</strong> is due today.`,
    sprint_delivered: `<strong>${userName}</strong> delivered <strong>${sprintTitle}</strong>. Accept it within 72 hours.`,
    sprint_slot_open: `A slot opened in <strong>${userName}</strong>'s sprint <strong>${sprintTitle}</strong>. Buy it within 48 hours.`,
    intro_video_ready: `Foti made the intro video of <strong>${sprintTitle}</strong>. Watch it and put it on your page.`,
    sprint_approved: `Your sprint <strong>${sprintTitle}</strong> is live.`,
    sprint_sent_back: `Staff sent your sprint <strong>${sprintTitle}</strong> back. Read the note.`,
    seat_approved: `Your advisor plan <strong>${seatTitle}</strong> is live.`,
    seat_sent_back: `Staff sent your advisor plan <strong>${seatTitle}</strong> back. Read the note.`,
  }
  if (!Object.keys(messages).includes(activity.value.action)) {
    return 'You have a new notification.'
  }
  return messages[activity.value.action]
})
const icon = computed(() => {
  const icons = {
    connection_request_received: {
      component: UserAddIcon,
      class: 'text-primary-500',
    },
    opportunity_accepted: {
      component: CheckIcon,
      class: 'text-success-500',
    },
    opportunity_applied: {
      component: CheckIcon,
      class: 'text-success-500',
    },
    opportunity_invited: {
      component: UserAddIcon,
      class: 'text-primary-500',
    },
    new_rank_achieved: {
      component: ChessKingIcon,
      class: 'text-secondary-500',
    },
    new_recommendation: {
      component: RecommendIcon,
      class: 'text-primary-500',
    },
    leave_review_reminder: {
      component: CommentAddIcon,
      class: 'text-primary-500',
    },
    leave_recommendation_reminder: {
      component: CommentAddIcon,
      class: 'text-primary-500',
    },
    session_rescheduled: {
      component: CalendarIcon,
      class: 'text-error-500',
    },
    session_rescheduled_mentee: {
      component: CalendarIcon,
      class: 'text-error-500',
    },
    session_declined: {
      component: NotAllowedIcon,
      class: 'text-neutral-500',
    },
    session_scheduled: {
      component: CalendarIcon,
      class: 'text-primary-500',
    },
    session_completed: {
      component: CheckIcon,
      class: 'text-success-500',
    },
    session_summary_ready: {
      component: CheckIcon,
      class: 'text-success-500',
    },
    memory_brief_changed: {
      component: CheckIcon,
      class: 'text-success-500',
    },
    session_pending_confirmation: {
      component: CalendarIcon,
      class: 'text-primary-500',
    },
    session_requested: {
      component: CalendarIcon,
      class: 'text-success-500',
    },
    team_member_joined: {
      component: PlusIcon,
      class: 'text-secondary-500',
    },
    content_library_added: {
      component: PlusIcon,
      class: 'text-success-500',
    },
    session_reviewed: {
      component: CommentAddIcon,
      class: 'text-primary-500',
    },
    invite_back_received: {
      component: CalendarIcon,
      class: 'text-primary-500',
    },
    session_declined_mentee: {
      component: NotAllowedIcon,
      class: 'text-neutral-500',
    },
    session_cancelled_membership_lapsed: {
      component: NotAllowedIcon,
      class: 'text-neutral-500',
    },
    session_auto_declined: {
      component: NotAllowedIcon,
      class: 'text-neutral-500',
    },
    session_expired_for_mentee: {
      component: NotAllowedIcon,
      class: 'text-neutral-500',
    },
    session_expired_for_mentor: {
      component: NotAllowedIcon,
      class: 'text-neutral-500',
    },
    opportunity_archived: {
      component: ArchiveIcon,
      class: 'text-neutral-500',
    },
    opportunity_disapproved: {
      component: NotAllowedIcon,
      class: 'text-error-500',
    },
    city_speaker_invited: {
      component: CalendarIcon,
      class: 'text-primary-500',
    },
    city_speaker_date_set: {
      component: CalendarIcon,
      class: 'text-primary-500',
    },
    city_speaker_accepted: {
      component: CheckIcon,
      class: 'text-success-500',
    },
    city_speaker_declined: {
      component: NotAllowedIcon,
      class: 'text-neutral-500',
    },
    guest_post_approved: {
      component: CheckIcon,
      class: 'text-success-500',
    },
    guest_post_changes_asked: {
      component: CommentAddIcon,
      class: 'text-warning-500',
    },
    guest_post_declined: {
      component: CommentAddIcon,
      class: 'text-neutral-500',
    },
    guest_post_live: {
      component: CheckIcon,
      class: 'text-success-500',
    },
    live_starting_soon: {
      component: CalendarIcon,
      class: 'text-success-500',
    },
    live_follower_alert: {
      component: CalendarIcon,
      class: 'text-success-500',
    },
    live_approved: {
      component: CheckIcon,
      class: 'text-success-500',
    },
    live_guest_invited: {
      component: CommentAddIcon,
      class: 'text-primary-500',
    },
    live_guest_accepted: {
      component: CheckIcon,
      class: 'text-success-500',
    },
    live_guest_declined: {
      component: CommentAddIcon,
      class: 'text-neutral-500',
    },
    live_guest_withdrew: {
      component: CommentAddIcon,
      class: 'text-neutral-500',
    },
    live_sent_back: {
      component: CommentAddIcon,
      class: 'text-warning-500',
    },
    films_ready: {
      component: CheckIcon,
      class: 'text-success-500',
    },
    intro_video_ready: {
      component: CheckIcon,
      class: 'text-success-500',
    },
    group_call_place_open: {
      component: CalendarIcon,
      class: 'text-success-500',
    },
    seat_place_open: {
      component: CalendarIcon,
      class: 'text-primary-500',
    },
    sprint_new_order: {
      component: CheckIcon,
      class: 'text-success-500',
    },
    sprint_deciding: {
      component: CalendarIcon,
      class: 'text-primary-500',
    },
    sprint_due: {
      component: CalendarIcon,
      class: 'text-warning-500',
    },
    sprint_delivered: {
      component: CheckIcon,
      class: 'text-success-500',
    },
    sprint_slot_open: {
      component: CalendarIcon,
      class: 'text-primary-500',
    },
    sprint_approved: {
      component: CheckIcon,
      class: 'text-success-500',
    },
    sprint_sent_back: {
      component: CommentAddIcon,
      class: 'text-warning-500',
    },
    seat_approved: {
      component: CheckIcon,
      class: 'text-success-500',
    },
    seat_sent_back: {
      component: CommentAddIcon,
      class: 'text-warning-500',
    },
  }
  if (!Object.keys(icons).includes(activity.value.action)) {
    return {
      component: NotAllowedIcon,
      class: 'text-neutral-500',
    }
  }
  return icons[activity.value.action]
})
const isUrlSameDomain = computed(() => {
  return activity.value.url?.includes(window.location.origin)
})
const componentName = computed(() => {
  if (!activity.value.url) {
    return 'div'
  }
  return isUrlSameDomain.value ? 'InertiaLink' : 'a'
})

const notificationsStore = useNotificationsStore()
const handleNotificationClick = () => {
  notificationsStore.markNotificationAsRead(props.notification.id)
}
</script>
